import { useRef, useState } from 'react';
import { Button, Tooltip } from '@nazka/nazka.mapframe.components';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import {
  RISK_COLORS,
  VIRIDIS_GRADIENT_STOPS,
  VIRIDIS_LABELS,
  VIRIDIS_MIN_MAX_LABELS,
} from '@App/pages/RiskDataPage/risk-data-page.constants';
import { useOnClickOutside } from '@services/hooks.service';
import { useTranslation } from '@utils/i18n/i18n.consumers';

import './risk-legend.styl';

const RISK_ATTRIBUTIONS = {
  'extreme-precipitation': [
    {
      label: 'Aqueduct Water Risk Atlas (2023)',
      href: 'http://doi.org/10.46830/writn.23.00061',
    },
  ],
  'extreme-wind': [
    {
      label: 'Bloemendaal et al., 2020',
      href: 'https://doi.org/10.1038/s41597-020-0381-2',
    },
    {
      label: 'Bloemendaal et al., 2022',
      href: 'https://doi.org/10.1126/sciadv.abm8438',
    },
  ],
  'heat-wave': [
    {
      label: 'ClimateScale',
      href: 'https://www.climatescale.com/',
    },
  ],
  fire: [
    {
      label: 'ClimateScale',
      href: 'https://www.climatescale.com/',
    },
  ],
  'dry-spell': [
    {
      label: 'ClimateScale',
      href: 'https://www.climatescale.com/',
    },
  ],
};

function getTitle(risk) {
  switch (risk) {
    case 'extreme-wind':
      return 'Wind speed in m/s';
    case 'extreme-precipitation':
      return 'flooding';
    default:
      return risk;
  }
}

function RiskLegend({ selectedRisk }) {
  const { t } = useTranslation();
  const [showAttribution, setShowAttribution] = useState(false);
  const attributionRef = useRef(null);
  const windLabels = VIRIDIS_LABELS;
  const compactWindLabels = VIRIDIS_MIN_MAX_LABELS;

  useOnClickOutside(attributionRef, () => setShowAttribution(false));

  const attributions = RISK_ATTRIBUTIONS[selectedRisk] || [];
  const tooltipId = `risk-legend-attribution-${selectedRisk}`;

  return (
    <div style={{ backgroundColor: selectedRisk === 'extreme-wind' ? 'rgba(255, 255, 255, 0.4)' : 'white' }} className="risk-legend">
      <div className="risk-legend__header">
        <h4>{t(getTitle(selectedRisk))}</h4>
        {attributions.length > 0 && (
          <div ref={attributionRef} className="risk-legend__attribution">
            <Tooltip
              id={tooltipId}
              isOpen={showAttribution}
              openOnClick
              clickable
              place="right"
              className="risk-legend__attribution__tooltip"
              tooltip={(
                <div
                  className="risk-legend__attribution__content"
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                >
                  {attributions.map(({ label, href }) => (
                    <a
                      key={label}
                      className="risk-legend__attribution__link"
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (!href) {
                          e.preventDefault();
                        }
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}
            >
              <Button
                icon={faInfoCircle}
                onClick={() => setShowAttribution(prev => !prev)}
                hover={false}
                rootElProps={{ 'data-tooltip-id': tooltipId }}
                className="risk-legend__attribution__button"
                layout="ghost"
                color="black"
                aria-label={t('Show dataset attribution')}
              />
            </Tooltip>
          </div>
        )}
      </div>
      {selectedRisk === 'extreme-wind' ? (
        <div className="risk-legend__viridis risk-legend__viridis--wind">
          <div
            className="risk-legend__viridis__scale"
            style={{ background: `linear-gradient(to top, ${VIRIDIS_GRADIENT_STOPS})` }}
          />
          <div className="risk-legend__viridis__labels risk-legend__viridis__labels--default">
            {windLabels.map(({ value, position }) => (
              <div
                key={value}
                className="risk-legend__viridis__labels__label"
                style={{ top: `${position}%` }}
              >
                {value}
              </div>
            ))}
          </div>
          <div className="risk-legend__viridis__labels risk-legend__viridis__labels--compact">
            {compactWindLabels.map(({ value, position }) => (
              <div
                key={value}
                className="risk-legend__viridis__labels__label"
                style={{ top: `${position}%` }}
              >
                {value}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {Object.keys(RISK_COLORS).map(risk => (
            <div className="risk-legend__item" key={risk}>
              <div className="risk-legend__item__color" style={{ backgroundColor: RISK_COLORS[risk] }} />
              <span>{t(risk)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default RiskLegend;
